/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const C = {
  navy: '#0F2742',
  navy3: '#1F3957',
  text: '#0B1F36',
  text2: '#475569',
  muted: '#64748B',
  border: '#E2E8F0',
  border2: '#CBD5E1',
  card: '#FFFFFF',
  bg: '#F8FAFC',
  amberBg: '#FEF3C7',
  amberBorder: '#FCD34D',
  blue: '#2563EB',
  rose: '#FB7185',
  roseBg: '#FFF1F2',
  roseBorder: '#FECDD3',
};

/**
 * Read-only catalogue panel for optional add-on rates that aren't part of
 * the standard refurb scope:
 *   • Damage Repairs (DR-*)         — fire / flood / storm / historic defects
 *   • Whole-House Composites (WH-*) — system-wide upgrades (rainwater goods, full CH)
 *
 * Surfaces the BCIS v1.1 library entries already seeded in `rate_cards`.
 * Shown collapsed by default to avoid overwhelming the project list — users
 * expand if they think one of these applies, then book a Project Cost
 * Review to scope it in.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OptionalAddonsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('damage_repairs');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/rates/optional-addons`).then(res => {
      if (!cancelled) {
        setData(res.data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data) return null;

  const dr = data.damage_repairs;
  const wh = data.whole_house_composites;
  // Hide-when-empty (Feb 2026) — if both catalogues are empty we render
  // nothing rather than a misleading "0 pre-priced items" panel.
  const totalItems = (dr?.items?.length || 0) + (wh?.items?.length || 0);
  if (totalItems === 0) return null;
  const active = tab === 'damage_repairs' ? dr : wh;

  return (
    <div data-testid="optional-addons-panel" style={{ marginTop: 32 }}>
      <button
        data-testid="optional-addons-toggle"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left',
          padding: '14px 18px', borderRadius: 10,
          background: open ? '#fff' : C.bg,
          border: `1.5px solid ${open ? C.navy : C.border}`,
          color: C.navy, cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 14, fontWeight: 700,
          transition: '0.15s',
        }}
      >
        <span>
          🛠️ Optional add-ons available — {dr.items.length + wh.items.length} pre-priced items
        </span>
        <span style={{ fontSize: 18, color: C.muted, fontWeight: 400 }}>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div
          data-testid="optional-addons-content"
          style={{
            marginTop: 8, padding: 18, background: '#fff',
            border: `1px solid ${C.border}`, borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.55, marginBottom: 14 }}>
            Standard SCOPE pricing covers a typical refurb. If your property has fire / flood / storm damage or you need a whole-house system upgrade, these line items are pre-priced. Flag the relevant ones to your QS during a Project Cost Review and they'll be added to your scope.
          </div>

          <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: `1px solid ${C.border}` }}>
            {[
              { id: 'damage_repairs', label: dr.label, n: dr.items.length },
              { id: 'whole_house_composites', label: wh.label, n: wh.items.length },
            ].map(t => (
              <button
                key={t.id}
                data-testid={`optional-addons-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                style={{
                  background: 'transparent', border: 'none',
                  padding: '10px 14px', cursor: 'pointer',
                  fontSize: 13, fontWeight: 700,
                  color: tab === t.id ? C.navy : C.muted,
                  borderBottom: tab === t.id ? `2px solid ${C.navy}` : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {t.label} <span style={{ color: C.muted, fontWeight: 500 }}>({t.n})</span>
              </button>
            ))}
          </div>

          <div style={{ fontSize: 12.5, color: C.text2, fontStyle: 'italic', marginBottom: 12 }}>
            {active.blurb}
          </div>

          <div
            style={{
              maxHeight: 380, overflowY: 'auto',
              border: `1px solid ${C.border}`, borderRadius: 8,
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead style={{ background: C.bg, position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, width: 80 }}>Code</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, width: 110 }}>Mid £</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, width: 70 }}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {active.items.map((it, idx) => (
                  <tr key={it.code} data-testid={`addon-row-${it.code}`} style={{ background: idx % 2 ? '#fff' : C.bg }}>
                    <td style={{ padding: '8px 12px', color: C.navy, fontWeight: 700, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{it.code}</td>
                    <td style={{ padding: '8px 12px', color: C.text2 }}>{it.description}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: C.navy, fontWeight: 600 }}>£{it.rate_gbp.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', color: C.muted }}>{it.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 14, fontSize: 12, color: C.muted }}>
            Rates above are the mid-market BCIS v1.1 baseline (May 2020 base + TPI uplift). Your QS will refine for region and specification during your call.
          </div>
        </div>
      )}
    </div>
  );
}
